/**
 * <GMDamageCalculator /> — Session 9 (spec 20).
 *
 * Debug tool for game masters. Uses the LIVE combat engine via
 * POST /api/admin/gm/damage-calc so numbers match what a real player sees
 * in combat. Never re-implements the damage formulas.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Skull, Sword, ChevronRight } from "lucide-react";
import { ELEMENTS } from "@/constants/elements";
import { ElementBadge } from "@/components/ElementBadge";

const CLASSES = ["Warrior", "Rogue", "Guardian", "Wizard", "Bard"];


export default function GMDamageCalculator() {
  const [attacker_class, setAttackerClass] = useState("Warrior");
  const [attacker_stats, setAttackerStats] = useState({
    strength: 20, defense: 10, dexterity: 10,
    intelligence: 10, charisma: 10, concentration: 10,
  });
  const [physical, setPhysical] = useState(50);
  const [element, setElement] = useState("");
  const [element_amount, setElementAmount] = useState(0);
  const [penetration, setPenetration] = useState(0);
  const [defender_affinity, setDefAffinity] = useState("");
  const [defender_armor, setDefArmor] = useState(0);
  const [crit_multiplier, setCrit] = useState(1.0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const res = await api.gmDamageCalc({
        attacker_class,
        attacker_stats,
        physical: Number(physical) || 0,
        element: element || null,
        element_amount: Number(element_amount) || 0,
        penetration: Number(penetration) || 0,
        defender_affinity: defender_affinity || null,
        defender_armor: Number(defender_armor) || 0,
        crit_multiplier: Number(crit_multiplier) || 1.0,
      });
      setResult(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calc failed");
    } finally {
      setBusy(false);
    }
  };

  const patchStat = (k, v) => setAttackerStats(s => ({ ...s, [k]: Number(v) || 0 }));

  return (
    <div className="space-y-3" data-testid="gm-damage-calculator">
      <div className="panel p-3">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] flex items-center gap-2 mb-2">
          <Zap className="w-4 h-4 text-[#B89C3A]" strokeWidth={1.5} />
          Combat Debug · Damage Calculator
        </div>
        <p className="italic text-[10px] text-[#666]">
          Every number here is produced by the same combat.calculate_damage the live game uses — no duplicate formulas.
        </p>
      </div>

      {/* ── Attacker ─────────────── */}
      <div className="panel p-3 space-y-2">
        <div className="font-heading text-[10px] tracking-widest uppercase text-[#E5C34A] flex items-center gap-1">
          <Sword size={12} strokeWidth={1.8} /> Attacker
        </div>
        <div className="grid grid-cols-2 gap-2">
          <LabelledSelect label="Class" value={attacker_class} onChange={setAttackerClass} options={CLASSES} testid="gm-calc-class" />
          <LabelledNumber label="Crit multiplier" value={crit_multiplier} onChange={setCrit} step={0.1} testid="gm-calc-crit" />
        </div>
        <div className="grid grid-cols-3 gap-1">
          {Object.entries(attacker_stats).map(([k, v]) => (
            <LabelledNumber key={k} label={k.slice(0, 3).toUpperCase()}
                            value={v} onChange={x => patchStat(k, x)}
                            testid={`gm-calc-stat-${k}`} />
          ))}
        </div>
      </div>

      {/* ── Damage packet ────────── */}
      <div className="panel p-3 space-y-2">
        <div className="font-heading text-[10px] tracking-widest uppercase text-[#E5C34A]">Damage Packet</div>
        <div className="grid grid-cols-2 gap-2">
          <LabelledNumber label="Physical" value={physical} onChange={setPhysical} testid="gm-calc-physical" />
          <LabelledNumber label="Penetration (0-1)" value={penetration} onChange={setPenetration} step={0.1} testid="gm-calc-pen" />
          <LabelledSelect label="Element" value={element} onChange={setElement}
                          options={["", ...ELEMENTS]} testid="gm-calc-element" />
          <LabelledNumber label="Element amount" value={element_amount} onChange={setElementAmount} testid="gm-calc-elamount" />
        </div>
      </div>

      {/* ── Defender ─────────────── */}
      <div className="panel p-3 space-y-2">
        <div className="font-heading text-[10px] tracking-widest uppercase text-[#E5C34A] flex items-center gap-1">
          <Skull size={12} strokeWidth={1.8} /> Defender
        </div>
        <div className="grid grid-cols-2 gap-2">
          <LabelledSelect label="Affinity" value={defender_affinity} onChange={setDefAffinity}
                          options={["", ...ELEMENTS]} testid="gm-calc-affinity" />
          <LabelledNumber label="Armor (physical)" value={defender_armor} onChange={setDefArmor} testid="gm-calc-armor" />
        </div>
      </div>

      <button
        data-testid="gm-calc-run"
        onClick={run}
        disabled={busy}
        className="w-full py-2 border border-[#E5C34A] text-[#E5C34A] hover:bg-[#B89C3A]/10 font-heading text-xs tracking-widest uppercase disabled:opacity-30">
        {busy ? "Running..." : "Calculate"}
      </button>

      {/* ── Result ───────────────── */}
      {result && (
        <div className="panel p-3 space-y-2" data-testid="gm-calc-result">
          <div className="flex items-center justify-between">
            <div className="font-heading text-xs tracking-widest uppercase text-[#8c8c8c]">Result</div>
            {result.result.crit && <span className="font-heading text-[10px] text-[#E33333]">CRITICAL</span>}
          </div>
          <div className="text-center py-3 border-y border-[#262626]">
            <div className="font-heading text-4xl text-[#E5C34A]">{result.result.final_damage}</div>
            <div className="font-mono text-[10px] text-[#8c8c8c] uppercase tracking-widest mt-1">final damage</div>
          </div>

          {result.result.physical > 0 && (
            <div className="font-mono text-[11px] text-[#B0ADA5]">
              Physical: <span className="text-[#E2DFD8]">{result.result.physical}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-1">
            {Object.entries(result.result.elemental || {}).map(([el, v]) => Number(v) > 0 && (
              <ElementBadge key={el} element={el} value={v} showLabel={false} size="sm" />
            ))}
          </div>

          {result.primary_stat_bonus && (
            <div className="font-mono text-[10px] text-[#B89C3A]">
              {result.primary_stat_bonus.label}: +{result.primary_stat_bonus.value}
            </div>
          )}

          <div className="pt-2 border-t border-[#262626]">
            <div className="font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c] mb-1">Breakdown</div>
            <div className="space-y-0.5">
              {result.result.breakdown.map((b, i) => (
                <div key={i} className="font-mono text-[10px] text-[#B0ADA5] flex items-center gap-1">
                  <ChevronRight size={9} strokeWidth={1.5} className="text-[#666]" />
                  <span className="flex-1">{b.label}</span>
                  <span className="text-[#8c8c8c]">→ {typeof b.total === "number" ? b.total.toFixed(1) : b.total}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function LabelledNumber({ label, value, onChange, step = 1, testid }) {
  return (
    <div>
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#666] mb-0.5">{label}</div>
      <input
        data-testid={testid}
        type="number" step={step} value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-1 text-xs text-[#E2DFD8] font-mono"
      />
    </div>
  );
}

function LabelledSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#666] mb-0.5">{label}</div>
      <select
        data-testid={testid}
        value={value} onChange={e => onChange(e.target.value)}
        className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-1 text-xs text-[#E2DFD8] font-mono">
        {options.map(o => <option key={o} value={o}>{o || "— none —"}</option>)}
      </select>
    </div>
  );
}
